import type { FormatLocaleObject } from "d3-format"
import { format as d3Format } from "d3-format"
import { getLocaleFormat } from "features/d3Config"

interface ValueFormatter {
  value: number
  prefix?: string
  suffix?: string
  decimalPlaces?: number
  trimZeros?: boolean
  formatUnits?: boolean
  locale?: string
  useScientificNotation?: boolean
}

interface CustomValueFormatter {
  prefix?: string
  suffix?: string
  decimalPlaces?: number
  trimZeros?: boolean
  formatUnits?: boolean
  locale?: string
  useScientificNotation?: boolean
}

interface SIPrefixFormatter {
  decimalPlaces?: number
  trimZeros?: boolean
  localeFormat: FormatLocaleObject
}
/**
 * Functions below are based on [giraffe/src/utils/formatters.ts](https://github.com/influxdata/giraffe/blob/f8b9698044c522ce27f0c8d722f090eebe692174/giraffe/src/utils/formatters.ts)
 */

export const siPrefixFormatter =
  ({
    decimalPlaces = 0,
    trimZeros = true,
    localeFormat,
  }: SIPrefixFormatter): ((val: number) => string) =>
  (val: number) => {
    // d3 cannot derive an SI exponent for 0, so formatPrefix returns "NaN" for it
    if (val === 0) {
      return localeFormat.format(`,.${decimalPlaces}${trimZeros ? "~" : ""}f`)(val)
    }
    const reference = val >= 10 ? val / 10 : val
    return localeFormat.formatPrefix(
      `,.${decimalPlaces}${trimZeros ? "~" : ""}`,
      reference,
    )(val)
  }

export const customValueFormatter =
  ({
    prefix = "",
    suffix = "",
    decimalPlaces = 0,
    trimZeros = true,
    formatUnits = true,
    locale = "en",
    useScientificNotation = false,
  }: CustomValueFormatter): ((value: number) => string) =>
  (value: number) =>
    valueFormatter({
      value,
      prefix,
      suffix,
      decimalPlaces,
      formatUnits,
      locale,
      trimZeros,
      useScientificNotation,
    })

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹"

// The whole exponent goes to superscript, otherwise the sign reads as a subtraction and the
// power as a separate number.
const toSuperscript = (exponent: string): string =>
  exponent
    .replace("+", "")
    .replace("-", "⁻")
    .replace(/\d/g, (digit) => SUPERSCRIPT_DIGITS[Number(digit)])

export const scientificFormatter = (
  val: number,
  decimalPlaces = 2,
  trimZeros = true,
  localeFormat: FormatLocaleObject = getLocaleFormat("en"),
): string => {
  if (val === 0) return "0"
  const exp = val.toExponential(decimalPlaces)
  const match = exp.match(/(-?[\d.]+)e([+-]?\d+)/)
  if (!match) return exp
  const [_, coeff, expNum] = match
  // The coefficient goes through the locale so it carries the right decimal separator and
  // the same minus sign as the rest of the board.
  const coefficient = localeFormat.format(`.${decimalPlaces}${trimZeros ? "~" : ""}f`)(
    Number(coeff),
  )
  return `${coefficient}×10${toSuperscript(expNum)}`
}

export const valueFormatter = ({
  value,
  prefix = "",
  suffix = "",
  decimalPlaces = 0,
  trimZeros = true,
  formatUnits = true,
  locale = "en",
  useScientificNotation = false,
}: ValueFormatter): string => {
  const localeFormat = getLocaleFormat(locale)

  const formatNumberOnly = (val: number) =>
    localeFormat.format(`,.${decimalPlaces}~f`)(val)

  const addSpace = (val: string) => val.replace(/(\d)(?=[^\d.,%])/g, "$1 ")

  // Use scientific notation for very small/large values if requested
  if (
    useScientificNotation &&
    ((Math.abs(value) < 1e-3 && value !== 0) || Math.abs(value) >= 1e3)
  ) {
    return `${prefix}${scientificFormatter(
      value,
      decimalPlaces,
      trimZeros,
      localeFormat,
    )}${suffix}`
  }

  if (formatUnits) {
    // code below shortens extremely large or small numbers (greater than septillion+) by
    // first converting number to SI format, then removing the SI unit to convert
    // number to scientific notation, and finally
    // adding yotta (Y) back
    if (value >= 1e30 || value <= -1e30) {
      const siFormattedValue = String(
        siPrefixFormatter({ decimalPlaces, trimZeros, localeFormat })(Math.abs(value)),
      ).slice(0, -1)
      return `${prefix}${value < 0 ? "-" : ""}${d3Format(`.${decimalPlaces}`)(
        Number(siFormattedValue),
      )} Y${suffix}`
    }

    return addSpace(
      `${prefix}${siPrefixFormatter({ decimalPlaces, trimZeros, localeFormat })(
        value,
      )}${suffix}`,
    )
  }
  return addSpace(`${prefix}${formatNumberOnly(value)}${suffix}`)
}
